/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// settings-dialog.js — the app Settings modal, opened from the top-right system
// (gear) icon or the application menu (menu:open-settings →
// chiphippo:open-settings). A renderer PopupManager modal, styled like the Rest
// Hippo settings dialog: a fixed master-detail card — a left nav rail of panels
// beside a single-column panel, a header with a close button, and pill-slider
// toggles.
//
// The dialog is deliberately dumb: it reads the current settings passed to
// open() and, on any change, broadcasts a `chiphippo:settings-changed`
// CustomEvent with a patch — app.js owns persistence (settings.set) and
// applying the change live. Add a new control to the panel + a case in app.js's
// applySettings; nothing else needs to know.

import { isWeb } from "../web-flavour.js";
import { el } from "../dom.js";
import { t, getLocales } from "../i18n.js";
import { PopupManager } from "../popup-manager.js";
import { LED_COLOR_OPTIONS } from "../catalog/parts.js";
import { LOGIC_FAMILIES, normalizeFamilyMode } from "../catalog/families.js";
import { baseUrlProblem, isKnownAiHost } from "../ai/base-url.js";
import { FONT_SIZES, normalizeFontSize } from "../font-scale.js";
import { buildColorSwatches } from "./color-swatches.js";
import { buildInfoButton, buildInfoMark } from "./info-button.js";
import { buildSegmented } from "./segmented-picker.js";
import { DatasheetDownloadDialog } from "./datasheet-download-dialog.js";
import { buildIntegrationPanel } from "./integration-settings.js";
import { buildSpicePanel } from "./settings-spice-panel.js";
import { protocolDocButton } from "./protocol-doc-button.js";

/** A line-drawn book glyph for the "browse the datasheet folder" affordance. */
const FOLDER_SVG =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" ' +
  'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
  'stroke-linejoin="round" aria-hidden="true"><path d="M22 19a2 2 0 0 1-2 ' +
  '2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>';

/** A line-drawn tray-and-arrow glyph for the "download the datasheets" action. */
const DOWNLOAD_SVG =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" ' +
  'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
  'stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>' +
  '<polyline points="7 10 12 15 17 10"/>' +
  '<line x1="12" y1="3" x2="12" y2="15"/></svg>';

/** Appearance ▸ Theme. "System" follows the OS; the other two pin it. The
    choice is persisted like any other setting, and MAIN acts on it — it
    becomes Electron's `nativeTheme.themeSource`, which every window's
    `prefers-color-scheme` (and the native menus/dialogs) then follow. */
const themeOptions = () => [
  { value: "system", label: t("settings.appearance.themeSystem") },
  { value: "light", label: t("settings.appearance.themeLight") },
  { value: "dark", label: t("settings.appearance.themeDark") },
];

/** Appearance ▸ Editor font size — the base of the app's type scale. Digits,
    not words: a size names itself the same in every language, so the track's
    width never depends on the locale (which "Small / Medium / Large" could not
    promise). The list is font-scale.js's, so this picker and the ⌥⌘=/− chord
    can never come to offer different steps. */
const fontSizeOptions = () =>
  FONT_SIZES.map((px) => ({ value: px, label: String(px) }));

/** Appearance ▸ Wire layout — how a NEWLY laid wire is drawn (see
    model/desk-doc.js's WIRE_LAYOUTS). Read at placement time, exactly as the
    default LED colour is: nothing already on the desk changes, and an existing
    wire's layout is its own Properties dialog's business. */
const wireLayoutOptions = () => [
  { value: "direct", label: t("settings.appearance.wireLayoutDirect") },
  { value: "routed", label: t("settings.appearance.wireLayoutRouted") },
];

/** Appearance ▸ Auto-close tray folders — whether opening a parts-tray
    section closes every one not on the way to it (PalettePanel.setAutoClose).
    On leads, as it does on About's own On/Off track. */
const trayAutoCloseOptions = () => [
  { value: true, label: t("settings.appearance.trayAutoCloseOn") },
  { value: false, label: t("settings.appearance.trayAutoCloseOff") },
];
/** Whether the toolbar offers the 3D view: On / Off, the tray row's shape. */
const view3dOptions = () => [
  { value: true, label: t("settings.appearance.view3dOn") },
  { value: false, label: t("settings.appearance.view3dOff") },
];
/** The parts tray's logic families (Feature 400). The two family names are
    part-number prefixes and stay as they are in every language; only "Both"
    is a word. Values are catalog/families.js `FAMILY_MODES`. */
const familyOptions = () => [
  ...LOGIC_FAMILIES.map((family) => ({ value: family, label: family })),
  { value: "combined", label: t("settings.datasheets.familyBoth") },
];

/** Unique ids for the notes below, so each (i) can `aria-controls` its own. */
let noteSeq = 0;

/**
 * A settings row whose label carries an (i) disclosing the row's note.
 *
 * The notes used to sit PERMANENTLY under their rows, and there are eight of
 * them across four tabs — several of them a full paragraph — so a panel read as
 * more prose than settings and the controls themselves were what you had to
 * hunt for. Disclosed instead: the explanation is one click away, beside the
 * thing it explains, and a panel you are not reading it in is a list of
 * controls.
 *
 * A POPOVER, not a tooltip — the same `components/info-button.js` the About
 * dialog's version details use, so the note is reachable by keyboard, stays put
 * while it is read, holds more than one paragraph, and is dismissed by Escape,
 * a click outside it, or the (i) again. It floats over the rows below rather
 * than pushing them down: a note that reflowed the panel would move the very
 * control the reader is about to reach for.
 *
 * @param {object} opts
 * @param {string} opts.label - the row's label text.
 * @param {HTMLElement} opts.control - what the row sets.
 * @param {string[]} opts.notes - one entry per paragraph of the note.
 * @param {string} [opts.htmlFor] - `for`, when the control has an id.
 * @param {boolean} [opts.stack] - lay the row out stacked (label above).
 * @returns {HTMLElement} the row, the note card positioned inside it.
 */
function rowWithNote({ label, control, notes, htmlFor, stack = false }) {
  const note = el(
    "div",
    {
      class: "settings-note",
      id: `settings-note-${++noteSeq}`,
      role: "note",
      hidden: true,
    },
    notes.map((text) => el("p", { class: "settings-hint", text })),
  );
  const info = buildInfoButton({
    target: note,
    label: t("settings.noteToggle", { label }),
  });
  return el(
    "div",
    { class: `settings-row${stack ? " settings-row--stack" : ""}` },
    [
      // The (i) is a SIBLING of the <label>, never inside it: a button in a
      // label is activated by clicking the label, so opening the note would
      // also toggle whatever the label points at.
      el("div", { class: "settings-label-group" }, [
        el("label", {
          class: "settings-label",
          text: label,
          ...(htmlFor ? { for: htmlFor } : {}),
        }),
        info,
      ]),
      control,
      // Inside the row so it can be positioned against it — and so hiding the
      // row (a store build hides two of them) takes its note with it.
      note,
    ],
  );
}

/** Emit a settings patch for app.js to persist + apply. */
function emitSettings(patch) {
  window.dispatchEvent(
    new CustomEvent("chiphippo:settings-changed", { detail: patch }),
  );
}

/**
 * Announce that the stored API key changed.
 *
 * The key is the one control here that bypasses the settings patch (it goes
 * straight to the OS-encrypted store), so a settings-changed event would never
 * carry it — and whether a key exists is exactly what decides if the toolbar's
 * AI segment is offered. The event says only THAT it changed: the value does
 * not cross the bridge, let alone the app.
 */
function announceKeyChange() {
  window.dispatchEvent(new CustomEvent("chiphippo:ai-key-changed"));
}

/**
 * Build the AI panel's rows once the provider list has arrived.
 *
 * The list comes from MAIN (`ai:providers`, projected from `app/ai/
 * providers.js`) rather than being restated here, so adding a third adapter
 * needs no renderer change and the picker can never offer one that does not
 * exist. That is also why this is built asynchronously: everything in the
 * panel — the placeholders, the key label — is the CHOSEN provider's data.
 *
 * @param {object} settings the current settings document.
 * @param {Array} providers `[{id, label, defaultBaseUrl, defaultModel, keyLabel}]`.
 * @param {(patch: object) => void} emitPatch the dialog's own patch emitter, so
 *   what this panel changes is remembered for a rebuild (SettingsDialog.#emit).
 * @returns {HTMLElement[]} the panel's children.
 */
function buildAiRows(settings, providers, emitPatch) {
  const bridge = window.chiphippo?.ai;
  // A patch replaces the `ai` object whole (the object-valued-setting
  // convention), so every control emits the WHOLE config, not its own field.
  let config = {
    provider: providers.some((p) => p.id === settings.ai?.provider)
      ? settings.ai.provider
      : providers[0].id,
    baseUrl: settings.ai?.baseUrl ?? "",
    model: settings.ai?.model ?? "",
  };
  const emit = (patch) => {
    config = { ...config, ...patch };
    emitPatch({ ai: { ...config } });
  };
  const chosen = () => providers.find((p) => p.id === config.provider);

  const status = el("p", { class: "settings-hint", text: "" });
  const keyInput = el("input", {
    class: "settings-text-input",
    type: "password",
    id: "set-ai-key",
    autocomplete: "off",
    spellcheck: false,
    placeholder: t("settings.ai.keyPlaceholder"),
  });
  const result = el("p", { class: "settings-hint", text: "" });

  /** Re-read whether a key is stored. The key itself never comes back. */
  const refreshStatus = async () => {
    const label = chosen()?.label ?? config.provider;
    let s;
    try {
      s = await bridge?.key?.status(config.provider);
    } catch {
      s = null;
    }
    if (s && s.encryptionAvailable === false) {
      status.textContent = t("settings.ai.noSecureStore");
      keyInput.disabled = true;
      return;
    }
    keyInput.disabled = false;
    // A web page has no OS keychain: the web build keeps the key in the
    // browser, and says so rather than claiming the OS's encryption.
    status.textContent = s?.configured
      ? t(isWeb() ? "settings.ai.keyStoredWeb" : "settings.ai.keyStored", {
          provider: label,
        })
      : t("settings.ai.keyMissing", { provider: label });
  };

  const baseUrl = el("input", {
    class: "settings-text-input",
    type: "text",
    id: "set-ai-base-url",
    spellcheck: false,
    value: config.baseUrl,
    // Commit on change (blur/Enter), never per keystroke — a half-typed URL
    // is not a setting, and this is written to disk.
    onChange: (e) => emit({ baseUrl: e.target.value.trim() }),
    // The mark follows every keystroke (it commits nothing).
    onInput: () => markBaseUrl(),
  });
  // An amber (i) beside the field when the key would go somewhere other than
  // a provider's own API or this machine — legal (a proxy, OpenRouter), but
  // worth saying where the key is going; red when main would refuse the
  // address outright (ai/base-url.js is the rule both sides read).
  const baseUrlMark = buildInfoMark({ label: "", variant: "advisory" });
  const markBaseUrl = () => {
    const url = baseUrl.value.trim() || chosen()?.defaultBaseUrl || "";
    const problem = url ? baseUrlProblem(url) : null;
    let note = "";
    if (problem === "insecure") note = t("settings.ai.baseUrlInsecure");
    else if (problem) note = t("settings.ai.baseUrlInvalid");
    else if (url && !isKnownAiHost(url)) {
      note = t("settings.ai.baseUrlUnknownHost", { host: new URL(url).host });
    }
    baseUrlMark.hidden = !note;
    baseUrlMark.title = note;
    baseUrlMark.setAttribute("aria-label", note);
    baseUrlMark.classList.toggle("info-btn--danger", Boolean(problem));
    baseUrlMark.classList.toggle("info-btn--advisory", !problem);
  };
  const model = el("input", {
    class: "settings-text-input",
    type: "text",
    id: "set-ai-model",
    spellcheck: false,
    value: config.model,
    onChange: (e) => emit({ model: e.target.value.trim() }),
  });

  const keyLabel = el("label", {
    class: "settings-label",
    for: "set-ai-key",
    text: t("settings.ai.apiKey"),
  });

  /** Placeholders show what an empty field falls back to. */
  const showDefaults = () => {
    const p = chosen();
    baseUrl.placeholder = p?.defaultBaseUrl ?? "";
    model.placeholder = p?.defaultModel ?? "";
    keyLabel.textContent = p?.keyLabel ?? t("settings.ai.apiKey");
    markBaseUrl();
  };

  const picker = buildSegmented({
    options: providers.map((p) => ({ value: p.id, label: p.label })),
    value: config.provider,
    ariaLabel: t("settings.ai.provider"),
    onPick: (provider) => {
      emit({ provider });
      showDefaults();
      result.textContent = "";
      refreshStatus();
    },
  });

  // The key is the ONE control that bypasses the settings patch entirely: it
  // goes straight to main's OS-encrypted store, because settings.json is
  // plaintext and is handed back to this window in full on every read.
  const saveKey = el("button", {
    class: "settings-action",
    type: "button",
    text: t("settings.ai.saveKey"),
    onClick: async () => {
      const value = keyInput.value.trim();
      if (!value) return;
      const r = await bridge?.key?.set(config.provider, value);
      keyInput.value = "";
      if (r && r.ok === false) {
        result.textContent = r.error ?? t("settings.ai.keyStoreFailed");
        return;
      }
      result.textContent = "";
      refreshStatus();
      announceKeyChange();
    },
  });
  const clearKey = el("button", {
    // Destructive, and irreversibly so: the key is write-only from here (it is
    // never read back into this window), so forgetting it means pasting it
    // again from wherever the user got it.
    class: "settings-action settings-action--danger",
    type: "button",
    text: t("settings.ai.clearKey"),
    onClick: async () => {
      await bridge?.key?.clear(config.provider);
      keyInput.value = "";
      result.textContent = "";
      refreshStatus();
      announceKeyChange();
    },
  });
  const testBtn = el("button", {
    class: "settings-action",
    type: "button",
    text: t("settings.ai.testConnection"),
    onClick: async () => {
      testBtn.disabled = true;
      result.textContent = t("settings.ai.testing");
      let r;
      try {
        r = await bridge?.test({ ...config });
      } catch (err) {
        r = { ok: false, error: String(err?.message ?? err) };
      }
      testBtn.disabled = false;
      result.textContent = r?.ok
        ? t("settings.ai.connected")
        : (r?.error ?? t("settings.ai.testFailed"));
    },
  });

  showDefaults();
  refreshStatus();

  return [
    // Both of the tab's notes hang off Provider — the row that names the
    // feature and the one every other row on the tab is a detail of. Said
    // HERE, where the connection is set up, because this is where the builder
    // is first met, long before a design lands on the desk.
    rowWithNote({
      label: t("settings.ai.provider"),
      control: picker,
      notes: [t("settings.ai.hint"), t("settings.ai.experimental")],
    }),
    // The four connection rows are INLINE — label left, field right — rather
    // than stacked. Stacked, each field cost two lines and the tab ran off the
    // bottom of the card before the key status was reached, which is the one
    // line here that answers a question the user actually asked.
    el("div", { class: "settings-row settings-row--field" }, [
      el("label", {
        class: "settings-label",
        for: "set-ai-base-url",
        text: t("settings.ai.baseUrl"),
      }),
      baseUrl,
      baseUrlMark,
    ]),
    el("div", { class: "settings-row settings-row--field" }, [
      el("label", {
        class: "settings-label",
        for: "set-ai-model",
        text: t("settings.ai.model"),
      }),
      model,
    ]),
    el("div", { class: "settings-row settings-row--field" }, [
      keyLabel,
      keyInput,
    ]),
    // The key's three buttons get their own row, under the field they act on —
    // inside it they would have had to share the line with the input.
    el("div", { class: "settings-row settings-row--actions" }, [
      clearKey,
      saveKey,
      testBtn,
    ]),
    status,
    result,
  ];
}

/** Settings ▸ About ▸ "Check automatically". An either/or, so the app's own
    either/or control — the same segmented track Theme and Wire layout use,
    rather than a checkbox this dialog has nowhere else. */
const autoUpdateOptions = () => [
  { value: true, label: t("settings.about.autoOn") },
  { value: false, label: t("settings.about.autoOff") },
];

/**
 * Build the About panel: the version, whether to check for updates
 * automatically, and an on-demand check.
 *
 * It is the one panel with LIVE state — a check reports itself over the
 * `chiphippo:updater-*` broadcasts, which arrive whether the check started
 * here, in the Help menu, or on its own at launch. Those listeners belong to
 * the dialog's OPEN lifetime, not to the session (the app-wide toasts are the
 * always-on surface), so this hands back a `dispose` for the close path.
 *
 * @param {object} settings the current settings document.
 * @param {(patch: object) => void} emitPatch the dialog's own patch emitter, so
 *   what this panel changes is remembered for a rebuild (SettingsDialog.#emit).
 * @returns {{ rows: HTMLElement[], dispose: () => void }}
 */
function buildAboutPanel(settings, emitPatch) {
  // The status line under the button. Blank until something happens: an
  // untouched panel has nothing to report, and a stale line from the last time
  // the dialog was open would be a claim about now.
  const status = el("p", {
    class: "settings-hint",
    role: "status",
    "aria-live": "polite",
    text: "",
  });
  const say = (text) => {
    status.textContent = text;
  };

  const version = el("span", {
    class: "settings-folder-path settings-folder-path--empty",
    text: t("settings.about.readingVersion"),
  });

  const checkBtn = el("button", {
    class: "settings-action",
    type: "button",
    text: t("settings.about.check"),
    onClick: () => {
      say(t("settings.about.checking"));
      window.chiphippo?.updater?.check?.();
    },
  });

  // Only ever shown once an update has actually been downloaded — restarting
  // is not something to offer before there is anything to restart INTO.
  const restartBtn = el("button", {
    class: "settings-action",
    type: "button",
    text: t("settings.about.restart"),
    hidden: true,
    onClick: () => window.chiphippo?.updater?.install?.(),
  });

  const autoPicker = buildSegmented({
    options: autoUpdateOptions(),
    value: settings.autoUpdateCheck === true,
    // Deliberately fuller than the visible label beside it: the row's own text
    // reads "Check automatically", which only makes sense next to the heading.
    ariaLabel: t("settings.about.autoAria"),
    onPick: (autoUpdateCheck) => emitPatch({ autoUpdateCheck }),
  });

  const autoRow = rowWithNote({
    label: t("settings.about.autoLabel"),
    control: autoPicker,
    notes: [t("settings.about.hint")],
  });
  const actionRow = el("div", { class: "settings-row settings-row--stack" }, [
    el("div", { class: "settings-folder-actions" }, [checkBtn, restartBtn]),
    status,
  ]);
  // The store-build message is NOT a note behind an (i): there, it is the only
  // thing on the panel — it explains why every control above it is gone — so a
  // reader would have nothing left to click the (i) beside.
  const storeHint = el("p", {
    class: "settings-hint",
    text: t("settings.about.storeHint"),
    hidden: true,
  });
  // A package manager's copy (the AUR's) is the same answer with a different
  // deliverer, and gets its own sentence — "the App Store" on Arch is a lie.
  const packageHint = el("p", {
    class: "settings-hint",
    text: t("settings.about.packageHint"),
    hidden: true,
  });
  // A Windows portable copy has no installer to update in place: its update is
  // a new download, which it can't fetch for itself.
  const portableHint = el("p", {
    class: "settings-hint",
    text: t("settings.about.portableHint"),
    hidden: true,
  });

  // Every lifecycle event, one handler each, so they can all be removed again.
  const onChecking = () => say(t("settings.about.checking"));
  const onAvailable = (e) => {
    const v = e.detail?.version;
    say(
      v
        ? t("settings.about.availableVersion", { version: v })
        : t("settings.about.available"),
    );
  };
  const onNotAvailable = (e) => {
    // "store-build" / "package-managed" / "dev-build" are ANSWERS, not
    // failures: this build cannot update itself, and saying so beside the
    // version is the honest report. None can reach here in practice — the
    // panel hides its controls for them below — but the check can also come
    // from the Help menu.
    const reason = e.detail?.reason;
    say(
      reason === "store-build"
        ? t("settings.about.storeBuild")
        : reason === "package-managed"
          ? t("settings.about.packageBuild")
          : reason === "portable"
            ? t("settings.about.portableBuild")
            : reason === "dev-build"
              ? t("settings.about.devBuild")
              : t("settings.about.upToDate"),
    );
  };
  const onDownloaded = (e) => {
    const v = e.detail?.version;
    say(
      v
        ? t("settings.about.readyVersion", { version: v })
        : t("settings.about.ready"),
    );
    restartBtn.hidden = false;
  };
  const onError = () => say(t("settings.about.error"));
  // Restart pressed while macOS is still taking the download (or Check
  // pressed during that wait, which answers this rather than checking).
  const onPreparing = () => say(t("settings.about.preparing"));

  const LISTENERS = [
    ["chiphippo:updater-checking", onChecking],
    ["chiphippo:updater-available", onAvailable],
    ["chiphippo:updater-not-available", onNotAvailable],
    ["chiphippo:updater-downloaded", onDownloaded],
    ["chiphippo:updater-error", onError],
    ["chiphippo:updater-preparing", onPreparing],
  ];
  for (const [event, fn] of LISTENERS) window.addEventListener(event, fn);

  // The version — and, with it, whether this build updates itself at all.
  // A store build is told BY MAIN (`distribution`), which is the only side
  // where `process.mas` is unambiguous; a store delivers its own updates (as
  // a package manager does — "package"), so every control here would be a
  // button that can't do anything.
  Promise.resolve(window.chiphippo?.getAppInfo?.() ?? null).then(
    (info) => {
      version.textContent = info?.version
        ? `Chip Hippo ${info.version}`
        : "Chip Hippo";
      version.classList.remove("settings-folder-path--empty");
      const hint = { store: storeHint, package: packageHint, portable: portableHint }[info?.distribution]; // prettier-ignore
      if (hint) {
        autoRow.hidden = true;
        actionRow.hidden = true;
        hint.hidden = false;
      }
    },
    (err) => {
      console.error("[renderer] app:info:get failed:", err);
      version.textContent = "Chip Hippo";
    },
  );

  return {
    rows: [
      el("div", { class: "settings-row settings-row--stack" }, [
        el("label", { class: "settings-label", text: t("settings.about.version") }), // prettier-ignore
        el("div", { class: "settings-folder-input" }, [version]),
      ]),
      autoRow,
      actionRow,
      storeHint,
      packageHint,
      portableHint,
    ],
    dispose: () => {
      for (const [event, fn] of LISTENERS)
        window.removeEventListener(event, fn);
    },
  };
}

/** The effective selection-border colour when none is stored (theme default). */
function themeSelectionColor() {
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue("--color-selection")
    .trim();
  return /^#[0-9a-f]{6}$/i.test(v) ? v : "#d0d0d0";
}

export class SettingsDialog {
  static #open = false;

  /**
   * The settings this dialog is showing, patched as it emits.
   *
   * `open()` is handed a snapshot, and a card that changes a setting then has
   * to be rebuilt (see `#relabel`) must not rebuild from the state it opened
   * in — the Language row would spring back to the language just left.
   */
  static #settings = {};

  /** The families the open project uses — Spice Lite always offers them. */
  static #projectFamilies = [];

  /** Which panel is showing, so a rebuild puts the user back on it. */
  static #tab = "appearance";

  /** True across a language rebuild, so `open()` keeps the tab rather than
      starting over on Appearance the way a fresh open should. */
  static #relabelling = false;

  /** Emit a settings patch for app.js to persist + apply — and keep our own
      copy current, since a rebuild is seeded from it. */
  static #emit(patch) {
    SettingsDialog.#settings = { ...SettingsDialog.#settings, ...patch };
    emitSettings(patch);
  }

  /**
   * Rebuild the whole card in the language just chosen.
   *
   * THE ONE DIALOG THAT HAS TO RELOCALIZE ITSELF. Every other transient
   * surface in the app — menus, popovers, notifications, the other dialogs —
   * is built when it opens and therefore speaks the current language for free.
   * This one is different for exactly one reason: the Language picker is
   * INSIDE it, so it is the only card that can still be on screen when the
   * language changes, and leaving it in the old one makes the setting look
   * like it did nothing.
   *
   * Closing and reopening rather than re-labelling in place, because the header
   * title, the nav rail, every row and the asynchronously-built AI panel are all
   * `t()` at build time — re-labelling would be a second, hand-maintained copy
   * of the whole card that could silently fall behind it. `PopupManager.close()`
   * runs `onClose` synchronously (dropping this listener and clearing the open
   * guard) before the queue is drained, so the reopen mounts cleanly; it is the
   * same close-then-open the Download button already uses.
   */
  static #relabel() {
    // MOUNTED, not merely open: `PopupManager.open` QUEUES a popup raised
    // behind another rather than stacking it, so the guard alone can be true
    // for a card that is not on screen — and `close()` would then dismiss
    // somebody else's popup and leave this one to mount in the old language.
    // Unreachable today (the picker that fires this is in this card, so the
    // card is showing), which is exactly why it is worth stating.
    if (!SettingsDialog.#open) return;
    if (!document.querySelector(".settings-popup")) return;
    const settings = SettingsDialog.#settings;
    SettingsDialog.#relabelling = true;
    try {
      PopupManager.close();
      SettingsDialog.open(settings);
    } finally {
      SettingsDialog.#relabelling = false;
    }
  }

  /**
   * Show the Settings dialog, seeded from `settings` (a no-op when already
   * open). @param {object} settings the current settings document.
   * @param {{tab?: string, projectFamilies?: Iterable<string>}} [opts] the
   *   panel to open on — Run's "settings need to be verified" and an
   *   element's "Manage connections…" open straight onto Serial I/O (key
   *   "integration") — and the families the open project uses, which Spice
   *   Lite's family strip always offers.
   */
  static open(settings = {}, { tab, projectFamilies } = {}) {
    if (SettingsDialog.#open) return;
    SettingsDialog.#open = true;
    SettingsDialog.#settings = { ...settings };
    // A language rebuild keeps the families it opened with.
    if (!SettingsDialog.#relabelling) {
      SettingsDialog.#projectFamilies = [...(projectFamilies ?? [])];
    }
    // A fresh open starts on Appearance (or the tab asked for); a language
    // rebuild resumes where the user was.
    if (!SettingsDialog.#relabelling) SettingsDialog.#tab = tab ?? "appearance";

    // The UI language. A <select> rather than a segmented track: eight choices
    // is past what one bordered row can show, and unlike Theme these are not an
    // either/or the user is comparing. "System" leads, then one row per shipped
    // catalog IN ITS OWN LANGUAGE — a language names itself the same way
    // whatever the UI is currently speaking, which is the whole reason a picker
    // is usable when you have accidentally set one you cannot read.
    //
    // The list comes off the loaded catalog payload (main's own table), so a
    // language cannot be offered here with no catalog behind it.
    const languageSelect = el(
      "select",
      {
        class: "settings-select",
        id: "set-language",
        onChange: (e) => SettingsDialog.#emit({ locale: e.target.value }),
      },
      [
        el("option", {
          value: "system",
          text: t("settings.appearance.languageSystem"),
          selected: (settings.locale ?? "system") === "system",
        }),
        ...getLocales().map((loc) =>
          el("option", {
            value: loc.code,
            text: loc.nativeName,
            selected: settings.locale === loc.code,
          }),
        ),
      ],
    );

    // `normalizeFontSize` IS the seeding fallback here, and a stronger one than
    // the `options.some(...) ? … : default` idiom its neighbours use: a stored
    // 15 shows as 16 rather than springing back to the default, so a
    // hand-edited settings.json is repaired toward what it asked for.
    const fontSizePicker = buildSegmented({
      options: fontSizeOptions(),
      value: normalizeFontSize(settings.fontSize),
      ariaLabel: t("settings.appearance.fontSize"),
      className: "segmented-picker--numeric",
      onPick: (fontSize) => SettingsDialog.#emit({ fontSize }),
    });

    const themePicker = buildSegmented({
      options: themeOptions(),
      value: themeOptions().some((o) => o.value === settings.theme)
        ? settings.theme
        : "system",
      ariaLabel: t("settings.appearance.theme"),
      onPick: (theme) => SettingsDialog.#emit({ theme }),
    });

    const selColor = el("input", {
      class: "settings-color",
      type: "color",
      id: "set-selection-color",
      value: settings.selectionColor || themeSelectionColor(),
      onInput: (e) => SettingsDialog.#emit({ selectionColor: e.target.value }),
    });

    const wireLayoutPicker = buildSegmented({
      options: wireLayoutOptions(),
      value: wireLayoutOptions().some(
        (o) => o.value === settings.defaultWireLayout,
      )
        ? settings.defaultWireLayout
        : "direct",
      ariaLabel: t("settings.appearance.wireLayout"),
      onPick: (defaultWireLayout) =>
        SettingsDialog.#emit({ defaultWireLayout }),
    });

    // Absent (or anything but `true`) is Off — the tray's behaviour before the
    // setting existed.
    const trayAutoClosePicker = buildSegmented({
      options: trayAutoCloseOptions(),
      value: settings.paletteAutoClose === true,
      ariaLabel: t("settings.appearance.trayAutoClose"),
      onPick: (paletteAutoClose) => SettingsDialog.#emit({ paletteAutoClose }),
    });

    // Whether the toolbar carries the 3D view's segment. Absent (or anything
    // but `true`) is Off, the default: the view is there to be switched on.
    const view3dPicker = buildSegmented({
      options: view3dOptions(),
      value: settings.view3dEnabled === true,
      ariaLabel: t("settings.appearance.view3d"),
      onPick: (view3dEnabled) => SettingsDialog.#emit({ view3dEnabled }),
    });

    // Settings ▸ Spice Lite (settings-spice-panel.js). Built from the
    // dialog's OWN copy, so a language rebuild keeps what was just applied.
    const spice = buildSpicePanel(
      SettingsDialog.#settings,
      SettingsDialog.#emit,
      {
        // prettier-ignore
        rowWithNote,
        projectFamilies: SettingsDialog.#projectFamilies,
      },
    );

    // Which family the parts tray shows. Live like the rest of the panel: the
    // tray rebuilds while this card is still open.
    const familyPicker = buildSegmented({
      options: familyOptions(),
      value: normalizeFamilyMode(settings.logicFamily),
      ariaLabel: t("settings.datasheets.family"),
      onPick: (logicFamily) => {
        SettingsDialog.#emit({ logicFamily });
        // Spice Lite's family strip follows what the tray shows.
        spice.setFamilyMode(logicFamily);
      },
    });

    const ledColorSwatches = buildColorSwatches({
      colors: LED_COLOR_OPTIONS,
      value: settings.defaultLedColor || "red",
      ariaLabel: t("settings.appearance.ledColor"),
      onPick: (color) => SettingsDialog.#emit({ defaultLedColor: color }),
    });

    // ── Data Sheets panel: the external datasheet-PDF folder ────────────────
    const hasDir =
      typeof settings.datasheetDir === "string" && settings.datasheetDir;
    const folderPath = el("span", {
      class: `settings-folder-path${hasDir ? "" : " settings-folder-path--empty"}`,
      text: hasDir ? settings.datasheetDir : t("settings.datasheets.noFolder"),
      title: hasDir ? settings.datasheetDir : "",
    });
    const clearBtn = el("button", {
      class: "settings-action settings-action--danger",
      type: "button",
      // A WORD, not a glyph: it sits beside Browse… and reads as its peer, the
      // same shape the AI tab's "Clear key" already uses.
      text: t("common.clear"),
      title: t("settings.datasheets.clearTitle"),
      hidden: !hasDir,
      onClick: () => {
        folderPath.textContent = t("settings.datasheets.noFolder");
        folderPath.title = "";
        folderPath.classList.add("settings-folder-path--empty");
        clearBtn.hidden = true;
        SettingsDialog.#emit({ datasheetDir: null });
      },
    });
    const browseBtn = el("button", {
      class: "settings-action",
      type: "button",
      title: t("settings.datasheets.browseTitle"),
      onClick: async () => {
        let dir;
        try {
          dir = await window.chiphippo?.settings?.chooseDatasheetDir?.();
        } catch (err) {
          console.error("[renderer] choose datasheet dir failed:", err);
          return;
        }
        if (!dir) return; // cancelled
        folderPath.textContent = dir;
        folderPath.title = dir;
        folderPath.classList.remove("settings-folder-path--empty");
        clearBtn.hidden = false;
        SettingsDialog.#emit({ datasheetDir: dir });
      },
    });
    // The glyph is markup, the label is text — so the icon goes in as HTML and
    // the word is APPENDED as a node rather than interpolated into the template
    // it used to share. A translation is user-visible text, and text that has
    // been through innerHTML is text that could carry markup.
    browseBtn.innerHTML = FOLDER_SVG;
    browseBtn.append(el("span", { text: t("common.browse") }));

    // Fill the app's own datasheet folder from the web and point the setting
    // above at it. This CLOSES the settings dialog first: PopupManager queues
    // a second popup rather than stacking it, so the progress window would
    // otherwise sit invisibly behind this one until it was dismissed.
    const downloadBtn = el("button", {
      class: "settings-action",
      type: "button",
      title: t("settings.datasheets.downloadTitle"),
      onClick: () => {
        PopupManager.close();
        DatasheetDownloadDialog.open();
      },
    });
    downloadBtn.innerHTML = DOWNLOAD_SVG;
    downloadBtn.append(el("span", { text: t("common.download") }));

    // The AI panel is filled in once main answers with the provider list, so
    // the dialog itself stays synchronous. It is never the open tab, so the
    // placeholder is not something a user normally sees.
    const aiPanel = el(
      "section",
      {
        class: "settings-panel",
        role: "tabpanel",
        "data-panel": "ai",
        hidden: true,
      },
      [el("p", { class: "settings-hint", text: t("settings.ai.loading") })],
    );
    Promise.resolve(window.chiphippo?.ai?.providers?.() ?? []).then(
      (providers) => {
        aiPanel.replaceChildren(
          ...(Array.isArray(providers) && providers.length
            ? buildAiRows(settings, providers, SettingsDialog.#emit)
            : [
                el("p", {
                  class: "settings-hint",
                  text: t("settings.ai.noProviders"),
                }),
              ]),
        );
      },
      (err) => {
        console.error("[renderer] ai:providers failed:", err);
      },
    );

    // Built before the panel map because it also hands back the teardown for
    // the updater listeners it attaches, which onClose below has to run.
    const about = buildAboutPanel(settings, SettingsDialog.#emit);

    // The Arduino serial integration's named connections. Built from the
    // dialog's OWN copy of the settings, so a language rebuild keeps what was
    // just applied; its drafts are the one thing on this card that is not a
    // live patch (integration-settings.js says why).
    const integration = buildIntegrationPanel(
      SettingsDialog.#settings,
      SettingsDialog.#emit,
      window.chiphippo,
    );

    const panels = {
      appearance: el(
        "section",
        {
          class: "settings-panel",
          role: "tabpanel",
          "data-panel": "appearance",
        },
        [
          rowWithNote({
            label: t("settings.appearance.language"),
            htmlFor: "set-language",
            control: languageSelect,
            notes: [t("settings.appearance.languageHint")],
          }),
          // Under Language, and ahead of Theme: the two settings that decide
          // how the app READS come first. Six segments is more than the
          // short either/or the picker was written for, and it is still the
          // right control — a <select> would make the one setting for text
          // you cannot read require opening a menu to read it.
          rowWithNote({
            label: t("settings.appearance.fontSize"),
            control: fontSizePicker,
            notes: [t("settings.appearance.fontSizeHint")],
          }),
          el("div", { class: "settings-row" }, [
            el("label", {
              class: "settings-label",
              text: t("settings.appearance.theme"),
            }),
            themePicker,
          ]),
          el("div", { class: "settings-row" }, [
            el("label", {
              class: "settings-label",
              for: "set-selection-color",
              text: t("settings.appearance.selectionColor"),
            }),
            selColor,
          ]),
          el("div", { class: "settings-row" }, [
            el("label", {
              class: "settings-label",
              text: t("settings.appearance.ledColor"),
            }),
            ledColorSwatches,
          ]),
          rowWithNote({
            label: t("settings.appearance.wireLayout"),
            control: wireLayoutPicker,
            notes: [t("settings.appearance.wireLayoutHint")],
          }),
          rowWithNote({
            label: t("settings.appearance.trayAutoClose"),
            control: trayAutoClosePicker,
            notes: [t("settings.appearance.trayAutoCloseHint")],
          }),
          rowWithNote({
            label: t("settings.appearance.view3d"),
            control: view3dPicker,
            notes: [t("settings.appearance.view3dHint")],
          }),
        ],
      ),
      datasheets: el(
        "section",
        {
          class: "settings-panel",
          role: "tabpanel",
          "data-panel": "datasheets",
          hidden: true,
        },
        [
          rowWithNote({
            label: t("settings.datasheets.family"),
            control: familyPicker,
            notes: [t("settings.datasheets.familyHint")],
          }),
          // The web build opens each part's datasheet from its manufacturer's
          // own site (pinout.js), so it offers neither the folder nor the
          // download: a browser could only read a folder for one visit, and
          // the manufacturers' servers do not answer a web page (CORS).
          !isWeb() &&
            rowWithNote({
              label: t("settings.datasheets.folder"),
              stack: true,
              control: el("div", { class: "settings-folder" }, [
                el("div", { class: "settings-folder-input" }, [folderPath]),
                el("div", { class: "settings-folder-actions" }, [
                  clearBtn,
                  browseBtn,
                ]),
              ]),
              notes: [t("settings.datasheets.folderHint")],
            }),
          !isWeb() &&
            rowWithNote({
              label: t("settings.datasheets.auto"),
              stack: true,
              control: el("div", { class: "settings-folder-actions" }, [
                downloadBtn,
              ]),
              notes: [t("settings.datasheets.autoHint")],
            }),
        ],
      ),
      spice: el(
        "section",
        {
          class: "settings-panel",
          role: "tabpanel",
          "data-panel": "spice",
          hidden: true,
        },
        spice.rows,
      ),
      ai: aiPanel,
      integration: el(
        "section",
        {
          class: "settings-panel",
          role: "tabpanel",
          "data-panel": "integration",
          hidden: true,
        },
        integration.rows,
      ),
      about: el(
        "section",
        {
          class: "settings-panel",
          role: "tabpanel",
          "data-panel": "about",
          hidden: true,
        },
        about.rows,
      ),
    };

    // Left nav rail — one item per panel; clicking switches the visible panel.
    // Serial I/O sits directly under Appearance. Its key stays "integration",
    // the name Run's refusal and an element's "Manage connections…" open it by.
    const TABS = [
      { key: "appearance", label: t("settings.nav.appearance") },
      { key: "integration", label: t("settings.nav.integration") },
      { key: "spice", label: t("settings.nav.spice") },
      { key: "datasheets", label: t("settings.nav.datasheets") },
      { key: "ai", label: t("settings.nav.ai") },
      { key: "about", label: t("settings.nav.about") },
    ];
    const navItems = TABS.map(({ key, label }) =>
      el("button", {
        class: "settings-nav-item",
        type: "button",
        role: "tab",
        "aria-selected": "false",
        "data-panel": key,
        text: label,
        onClick: () => select(key),
      }),
    );

    // The serial protocol reference, top right beside the ×. The header is
    // the whole card's, so the button belongs to the Serial I/O tab by being
    // shown only while that tab is.
    const protocolBtn = protocolDocButton(window.chiphippo);

    const select = (key) => {
      // Remembered on the class, not just in this closure: a language change
      // rebuilds the card and has to put the user back on the panel they were
      // reading.
      SettingsDialog.#tab = key;
      for (const item of navItems) {
        const on = item.getAttribute("data-panel") === key;
        item.classList.toggle("settings-nav-item--active", on);
        item.setAttribute("aria-selected", String(on));
      }
      for (const [panelKey, panel] of Object.entries(panels)) {
        panel.hidden = panelKey !== key;
      }
      protocolBtn.hidden = key !== "integration";
    };
    // Apply the starting panel through the same one path a click takes, so the
    // nav rail and the panels cannot disagree about which is showing.
    select(panels[SettingsDialog.#tab] ? SettingsDialog.#tab : "appearance");

    const body = [
      el("nav", { class: "settings-nav", role: "tablist" }, navItems),
      el("div", { class: "settings-panels" }, [
        panels.appearance,
        panels.integration,
        panels.spice,
        panels.datasheets,
        panels.ai,
        panels.about,
      ]),
    ];

    // The Language picker is in THIS card, so this is the one dialog that can
    // still be on screen when the language changes. app.js fires this only once
    // the new catalog is loaded, so the rebuild below reads the new one.
    const onLocaleChanged = () => SettingsDialog.#relabel();
    window.addEventListener("chiphippo:locale-changed", onLocaleChanged);

    // onClose fires only when THIS popup closes (not when a popup it was queued
    // behind closes), so the guard never resets while the dialog is still up.
    PopupManager.dialog({
      title: t("settings.title"),
      closeAriaLabel: t("settings.close"),
      className: "settings-popup",
      bodyClass: "settings-popup-body",
      body,
      headerActions: [protocolBtn],
      onClose: () => {
        // The About panel's updater listeners live for exactly as long as the
        // dialog does — the app-wide toasts are the always-on surface, and a
        // status line writing into a card that has been thrown away is a leak
        // that grows by one set every time Settings is opened.
        about.dispose();
        window.removeEventListener("chiphippo:locale-changed", onLocaleChanged);
        SettingsDialog.#open = false;
      },
    });
  }
}
